import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Info, ExternalLink, Loader2, Star } from 'lucide-react';
import { AdminBar, EditButton, DeleteButton } from '@/components/AdminBar';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_BADGES = {
  human_clinical: { label: 'Studio clinico umano', color: 'bg-[#5f7d52]/15 text-[#4a5f43]' },
  animal: { label: 'Studio animale', color: 'bg-amber-100 text-amber-700' },
  in_vitro: { label: 'In vitro', color: 'bg-blue-100 text-blue-700' },
  review: { label: 'Review', color: 'bg-gray-100 text-gray-600' },
  meta_analysis: { label: 'Meta-analisi', color: 'bg-gray-100 text-gray-600' },
};

export default function ScientificStudiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  const [filters, setFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState('Tutti');
  const [loading, setLoading] = useState(true);
  const canEdit = user?.is_admin || user?.is_editor || user?.role === 'admin' || user?.role === 'editor';

  const fetchStudies = async (area = '') => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/studies${area && area !== 'Tutti' ? `?area=${encodeURIComponent(area)}` : ''}`);
      setStudies(resp.data.studies);
      if (resp.data.filters) setFilters(resp.data.filters);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { fetchStudies(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Spostare nel cestino?')) return;
    try {
      const token = localStorage.getItem('biounica_token');
      await axios.delete(`${API}/editor/studies/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Spostato nel cestino');
      fetchStudies(activeFilter);
    } catch { toast.error('Errore'); }
  };

  const handleFilter = (f) => {
    setActiveFilter(f);
    fetchStudies(f);
  };

  return (
    <div className="max-w-4xl" data-testid="studies-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Approfondimenti scientifici</h1>
        <p className="text-sm text-[#4a4238] mt-2 max-w-2xl leading-relaxed">
          Evidenze e meccanismi d'azione di acidi umici e fulvici: riferimenti bibliografici e studi di riferimento
        </p>
      </div>

      {/* Disclaimer */}
      <Card className="rounded-xl border-gray-200 bg-gray-50 mb-6" data-testid="studies-disclaimer">
        <CardContent className="p-4 flex gap-3">
          <Info className="w-5 h-5 text-[#4a4238] shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-[#4a4238] leading-relaxed">
            Gli studi citati riguardano gli acidi umici e fulvici come classe di molecole. UMIFUL è un nutraceutico, non un farmaco. Le evidenze riportate sono a scopo informativo professionale e non costituiscono claim terapeutici.
          </p>
        </CardContent>
      </Card>

      {/* Admin Bar */}
      <AdminBar section="studies" onAdd={() => navigate('/admin', { state: { tab: 'studies', action: 'new' } })} />

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide" style={{ scrollbarWidth: 'none' }} data-testid="study-filters">
        {filters.map(f => (
          <button key={f} onClick={() => handleFilter(f)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              activeFilter === f ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200 hover:border-[#5f7d52]'
            }`} data-testid={`filter-${f}`}>{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#12100c]" /></div>
      ) : (
        <div className="space-y-4">
          {studies.map(study => {
            const typeBadge = TYPE_BADGES[study.study_type] || TYPE_BADGES.review;
            return (
              <Card key={study.id} className="rounded-2xl border-gray-100 shadow-sm" data-testid={`study-${study.id}`}>
                <CardContent className="p-5 sm:p-6">
                  {/* Header */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge className={`text-[10px] border-none font-semibold ${typeBadge.color}`}>{typeBadge.label}</Badge>
                    <div className="flex items-center gap-0.5">
                      {[...Array(study.relevance_score || 0)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-[#c6a15b] text-[#8a6620]" />
                      ))}
                      {[...Array(3 - (study.relevance_score || 0))].map((_, i) => (
                        <Star key={i} className="w-3 h-3 text-gray-200" />
                      ))}
                    </div>
                    {(study.area_tags || []).map(t => (
                      <Badge key={t} variant="outline" className="text-[9px] border-gray-200 text-[#4a4238]">{t}</Badge>
                    ))}
                    {canEdit && <div className="ml-auto flex gap-0.5"><EditButton onClick={() => navigate('/admin', { state: { tab: 'studies' } })} /><DeleteButton onClick={() => handleDelete(study.id)} /></div>}
                  </div>

                  {/* Title & Meta */}
                  <h3 className="font-semibold text-sm text-[#12100c] leading-snug mb-1">{study.title}</h3>
                  <p className="text-xs text-[#4a4238] mb-3">{study.authors} ({study.year}), <span className="italic">{study.journal}</span></p>

                  {/* Key Finding */}
                  <div className="bg-[#5f7d52]/5 border-l-3 border-[#5f7d52] p-3 rounded-r-lg mb-3" style={{ borderLeftWidth: '3px' }}>
                    <p className="text-xs font-semibold text-[#4a5f43] uppercase tracking-wide mb-1">Risultato chiave</p>
                    <p className="text-sm text-[#12100c] leading-relaxed">{study.key_finding}</p>
                  </div>

                  {/* Summary (accordion) */}
                  <Accordion type="single" collapsible>
                    <AccordionItem value="summary" className="border-none">
                      <AccordionTrigger className="py-2 text-xs font-medium text-[#4a5f43] hover:no-underline">
                        Leggi sintesi completa
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-[#4a4238] leading-relaxed pb-2">
                        {study.summary_it}
                        {study.methodology && (
                          <p className="mt-2 text-xs text-[#4a4238]/70"><span className="font-semibold">Metodologia:</span> {study.methodology}</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* DOI Link */}
                  {(study.doi || study.pubmed_url) && (
                    <a href={study.doi || study.pubmed_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#4a5f43] hover:underline mt-1">
                      Vai allo studio originale <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
